"use client"

import Link from "next/link"
import Image from "next/image"

import { useLocale } from "@/lib/locale-context"
import { type Project, projects } from "@/lib/projects"
import { cn } from "@/lib/utils"
import { Reveal } from "@/components/reveal"
import { SectionHead } from "@/components/section-head"

function Status({ project }: { project: Project }) {
  const { t } = useLocale()
  const label =
    project.status === "active"
      ? t.projects.statusActive
      : project.status === "in-progress"
        ? t.projects.statusInProgress
        : t.projects.statusArchived
  return (
    <span className="t-small inline-flex items-center gap-2 text-muted-foreground">
      <span
        className={cn(
          "inline-block h-2 w-2",
          project.status === "active" && "bg-accent",
          project.status === "in-progress" && "border border-accent",
          project.status === "archived" && "bg-muted-foreground/50"
        )}
        aria-hidden="true"
      />
      {label}
    </span>
  )
}

function Tech({ tech }: { tech: string[] }) {
  if (!tech.length) return null
  return (
    <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1">
      {tech.map((item) => (
        <li key={item} className="t-small text-muted-foreground">
          {item}
        </li>
      ))}
    </ul>
  )
}

/** A plate that has no photograph yet says so, rather than faking one. */
function Plate({ project, label }: { project: Project; label: string }) {
  const { locale } = useLocale()
  if (!project.coverImg) {
    return (
      <div className="flex aspect-[16/10] w-full items-center justify-center border border-border bg-secondary">
        <span className="t-small uppercase tracking-[0.06em] text-muted-foreground">
          {label}
        </span>
      </div>
    )
  }
  return (
    <Image
      src={project.coverImg}
      alt={`${project.title[locale]} interface`}
      width={1600}
      height={1000}
      sizes="(max-width: 768px) 100vw, 46vw"
      className="reveal-plate aspect-[16/10] w-full border border-border object-cover object-top transition-[filter,transform] duration-700 group-hover:grayscale-0"
    />
  )
}

export function SectionProjects() {
  const { locale, t, href: localHref } = useLocale()
  const featured = projects.filter((p) => p.featured)
  const rest = projects.filter((p) => !p.featured)

  const href = (project: Project) =>
    project.internal ? localHref(`/projects/${project.id}`) : (project.link ?? "")
  const external = (project: Project) =>
    project.internal ? {} : { target: "_blank", rel: "noopener noreferrer" }

  return (
    <section
      id="projects"
      className="shell scroll-mt-16 py-12 md:py-24"
      aria-labelledby="projects-heading"
    >
      <SectionHead id="projects" index="03" title={t.projects.title} lead={t.projects.lead} />

      {/* Featured: two plates, side by side */}
      <Reveal className="grid12 mt-8 md:mt-16">
        <span
          className="reveal-rule col-span-12 mb-4 block h-[2px] bg-foreground"
          aria-hidden="true"
        />
        <p className="reveal t-small col-span-12 mb-6 font-medium uppercase tracking-[0.06em] text-muted-foreground md:mb-8">
          {t.projects.featuredLabel}
        </p>

        {featured.map((project, i) => (
          <article
            key={project.id}
            className="reveal col-span-12 mb-10 md:col-span-6 md:mb-0"
            style={{ ["--d" as string]: `${i * 120}ms` }}
          >
            <Link href={href(project)} className="group block" {...external(project)}>
              <Plate project={project} label={t.projects.statusInProgress} />
              <p className="t-small mt-5 tabular-nums text-accent">{`0${i + 1}`}</p>
              <h3 className="t-lg mt-1.5 text-foreground transition-colors duration-300 group-hover:text-accent">
                {project.title[locale]}
              </h3>
              <div className="mt-3">
                <Status project={project} />
              </div>
              <p className="t-body mt-4 max-w-[46ch] text-muted-foreground">
                {project.description[locale]}
              </p>
              <Tech tech={project.tech} />
              <span className="t-small mt-5 inline-flex items-center gap-2 font-medium uppercase tracking-[0.06em] text-foreground">
                {t.projects.viewProject}
                <span
                  className="inline-block transition-transform duration-300 group-hover:translate-x-1.5"
                  aria-hidden="true"
                >
                  &#8594;
                </span>
              </span>
            </Link>
          </article>
        ))}
      </Reveal>

      {/* Everything else: compact rows on the same grid */}
      <Reveal className="grid12 mt-14 md:mt-20">
        <span
          className="reveal-rule col-span-12 mb-4 block h-[2px] bg-foreground"
          aria-hidden="true"
        />
        <p className="reveal t-small col-span-12 font-medium uppercase tracking-[0.06em] text-muted-foreground">
          {t.projects.moreLabel}
        </p>
      </Reveal>

      <div className="mt-2">
        {rest.map((project, i) => (
          <Reveal key={project.id} className="row-mark rule">
            <Link
              href={href(project)}
              className="group grid12 items-baseline py-5 md:py-6"
              {...external(project)}
            >
              <div className="reveal col-span-12 flex items-baseline gap-4 md:col-span-4">
                <span className="t-small tabular-nums text-accent">
                  {`0${featured.length + i + 1}`}
                </span>
                <h3 className="t-md text-foreground transition-colors duration-300 group-hover:text-accent">
                  {project.title[locale]}
                </h3>
              </div>
              <div className="reveal col-span-12 mt-2 md:col-span-4 md:mt-0">
                <Status project={project} />
              </div>
              <div className="reveal col-span-12 mt-2 flex items-baseline justify-between gap-4 md:col-span-4 md:mt-0">
                <ul className="flex flex-wrap gap-x-4 gap-y-1">
                  {project.tech.slice(0, 3).map((item) => (
                    <li key={item} className="t-small text-muted-foreground">
                      {item}
                    </li>
                  ))}
                </ul>
                <span
                  className="t-small shrink-0 text-foreground transition-transform duration-300 group-hover:translate-x-1.5"
                  aria-hidden="true"
                >
                  &#8594;
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  )
}
